import AddressList from '@app/components/AddressList'
import CheckBox from '@app/components/CheckBox'
import CompanySelector from '@app/components/CompanySelector'
import ContactProfileImage from '@app/components/ContactProfileImage'
import DateSelector from '@app/components/DateSelector'
import EditPageContainer from '@app/components/EditPageContainer'
import EmailAddressList from '@app/components/EmailAddressList'
import { FileDropzone } from '@app/components/FileUpload'
import PhoneNumberList from '@app/components/PhoneNumberList'
import { SocialConnectionEditor } from '@app/components/SocialConnection'
import { ICurrentUserState } from '@app/store/modules/account'
import {
  IGlobalCompaniesState,
  IGlobalCompanyContactsState,
  IGlobalGroupsState,
  IGlobalSocialNetworksState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import Select, { MultiValue } from 'react-select'
import Creatable from 'react-select/creatable'
import { Input } from 'reactstrap'
import { actionCreators, IConnectionEditState } from '../modules/connectionEdit'
import { IAssociatedGroup } from '@app/models/AssociatedGroup'
import { CompanyContact } from '@app/models/CompanyContact'
import { Company } from '@app/models/Company'

interface IConnectionViewRouterProps {
  id: string
}

type IComponentProps = IConnectionEditState &
  ICurrentUserState &
  IGlobalCompanyContactsState &
  IGlobalCompaniesState &
  IGlobalGroupsState &
  IGlobalSocialNetworksState &
  RouteComponentProps<IConnectionViewRouterProps> &
  typeof actionCreators

export default class ConnectionEditPage extends React.PureComponent<
  IComponentProps
> {
  public connectionId: string

  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)
  }

  public componentDidMount() {
    const { id } = this.props.match.params

    this.connectionId = id

    this.props.loadConnection(id)
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (!this.props.connection) {
      return <div />
    }

    return (
      <EditPageContainer
        saveDisabled={!this.props.canSave}
        title='New Connection'
        onSaveClick={this._saveConnection}
        onCancelClick={this._cancel}
      >
        <div className='container my-2'>
          {this._renderBasicInfo()}
          <h5 className='section-title'>Groups</h5>
          {this._renderGroups()}
          <h5 className='section-title'>Phone Numbers</h5>
          {this._renderPhoneNumbers()}
          <h5 className='section-title'>Email Addresses</h5>
          {this._renderEmailAddresses()}
          <h5 className='section-title mr-2'>Addresses</h5>
          {this._renderAddresses()}
          <h5 className='section-title'>Social</h5>
          {this._renderSocial()}
          <h5 className='section-title'>Public Notes</h5>
          {this._renderPublicNotes()}
          <h5 className='section-title'>My Notes</h5>
          {this._renderMyNotes()}
          <h5 className='section-title'>Related Connections</h5>
          {this._renderRelatedConnections()}
        </div>
      </EditPageContainer>
    )
  }

  private _saveConnection() {
    this.props.save(this.connectionId)
  }

  private _cancel() {
    this.props.history.goBack()
  }

  private _renderBasicInfo() {
    if (this.props.connection.isCompany) {
      return this._renderCompanyInfo()
    } else {
      return (
        <div>
          <div className={`d-flex flex-row`}>
            <div className='mr-2 mt-2'>{this._renderProfileImage()}</div>
            <div className='mr-2 flex-grow-1'>
              <Input
                placeholder='First'
                tabIndex={1}
                value={this.props.connection.firstName}
                onChange={(e) => this.props.setFirstName(e.target.value)}
              />

              <Input
                className='mt-2'
                placeholder='Title'
                tabIndex={3}
                value={this.props.connection.title}
                onChange={(e) => this.props.setTitle(e.target.value)}
              />
              <div className='my-3'>
                <CheckBox
                  tabIndex={5}
                  onChange={(e) => this.props.setIsCompany(e)}
                  value={this.props.connection.isCompany}
                />{' '}
                Is Company
              </div>
            </div>
            <div className='flex-grow-1'>
              <Input
                placeholder='Last'
                tabIndex={2}
                className='mb-2'
                value={this.props.connection.lastName}
                onChange={(e) => this.props.setLastName(e.target.value)}
              />

              <CompanySelector
                tabIndex={4}
                onChange={this.props.setCompany}
                placeholder='Company'
                value={this.props.connection.company}
              />
            </div>
          </div>
        </div>
      )
    }
  }

  private _renderProfileImage() {
    const selectPhotoStyle: React.CSSProperties = {
      borderWidth: '0px',
    }

    return (
      <FileDropzone style={selectPhotoStyle} identifier='profile-image-editor'>
        <ContactProfileImage contact={this.props.connection} size={70} />
      </FileDropzone>
    )
  }

  private _renderCompanyInfo() {
    return (
      <div>
        <div className={`d-flex flex-row`}>
          <div className='mr-2 align-self-center'>
            {this._renderProfileImage()}
          </div>
          <div className='mr-2 flex-grow-1 align-self-center'>
            <Input
              placeholder='Company'
              className='mt-2'
              value={this.props.connection.lastName}
              onChange={(e) => this.props.setLastName(e.target.value)}
            />
          </div>
          <div className='flex-grow-1 align-self-center'>
            <Input
              className='mt-2'
              placeholder='Title'
              value={this.props.connection.title}
              onChange={(e) => this.props.setTitle(e.target.value)}
            />
          </div>
        </div>
        <div className='float-right'>
          <CheckBox
            onChange={(e) => this.props.setIsCompany(e)}
            value={this.props.connection.isCompany}
          />{' '}
          Is Company
        </div>
      </div>
    )
  }

  private _renderGroups() {
    return (
      <div className='ml-3'>
        <Creatable
          name='groups-list'
          value={
            this.props.connection.groups.toArray() as MultiValue<
              IAssociatedGroup
            >
          }
          isMulti
          getOptionLabel={(opt) => opt.name}
          getOptionValue={(opt) => opt.groupId}
          placeholder='Add a group...'
          options={this.props.groups}
          onChange={(e: any) => {
            this.props.setSelectedGroups(e)
          }}
        />
      </div>
    )
  }

  private _renderPhoneNumbers() {
    return (
      <div className='ml-3'>
        <PhoneNumberList
          dataSource={this.props.connection.phoneNumbers}
          onChange={(e: any) => this.props.setPhoneNumbers(e)}
        />
      </div>
    )
  }

  private _renderEmailAddresses() {
    return (
      <div className='ml-3'>
        <EmailAddressList
          dataSource={this.props.connection.emailAddresses}
          onChange={(e: any) => this.props.setEmailAddresses(e)}
        />
      </div>
    )
  }

  private _renderAddresses() {
    return (
      <div className='ml-3'>
        <AddressList
          onChange={(e: any) => this.props.setAddresses(e)}
          dataSource={this.props.connection.addresses}
        />
      </div>
    )
  }

  private _renderSocial() {
    return (
      <div className='ml-3'>
        <div className='d-flex flex-column'>
          {this.props.socialNetworks.map((s, i) => {
            if (s === undefined || i === undefined) {
              return null
            }

            const match = this.props.connection.social.find((item: any) => {
              return item.type === s.id
            })

            return (
              <SocialConnectionEditor
                className='d-flex flex-row align-items-center mt-2'
                userName={match ? match.username : ''}
                key={`social-${i}`}
                socialConnection={s}
                onChange={this.props.setSocial}
              />
            )
          })}
          <div className='d-flex flex-row mt-2'>
            <FontAwesomeIcon
              icon={['fal', 'browser']}
              fixedWidth
              style={{ fontSize: '2rem' }}
            />
            <Input
              placeholder='www.example.com'
              value={this.props.connection.url}
              onChange={(e) => this.props.setWebsite(e.target.value)}
            />
          </div>
          {!this.props.connection.isCompany && (
            <div className='d-flex flex-row mt-2'>
              <FontAwesomeIcon
                icon={['fal', 'gift']}
                fixedWidth
                style={{ fontSize: '2rem' }}
              />
              <DateSelector
                value={this.props.connection.birthday}
                onChange={(e) => this.props.setBirthday(e)}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  private _renderPublicNotes() {
    return (
      <div className='ml-3'>
        <Input
          type='textarea'
          value={this.props.connection.note}
          onChange={(e) => this.props.setNote(e.target.value)}
        />
      </div>
    )
  }

  private _renderMyNotes() {
    return (
      <div className='ml-3'>
        <Input
          type='textarea'
          value={
            this.props.connection.privateNote
              ? this.props.connection.privateNote.note
              : ''
          }
          onChange={(e) => this.props.setPrivateNote(e.target.value)}
        />
      </div>
    )
  }

  private _renderRelatedConnections() {
    return (
      <div className='ml-3'>
        <Select
          name='connections-list'
          value={this.props.selectedConnections as MultiValue<CompanyContact>}
          isMulti
          getOptionLabel={(opt) => opt.name}
          getOptionValue={(opt) => opt.contactId}
          placeholder='Add related connections...'
          options={this.props.companyContacts.results.toArray() as CompanyContact[]}
          onChange={(e: any) => {
            this.props.setSelectedConnections(e)
          }}
        />
      </div>
    )
  }
}
